import { Table, Checkbox, Text, Button, Flex, TextField } from '@radix-ui/themes';
import { useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PAGINATED_TEMPLATES } from '../../query/paginatedTemplate';
import { Pagination } from '../../../elements/Pagination';
import { getManagerAccessToken } from '../../../accesstoken';
import { useDebounce } from '../../../../hooks/useDebounce';
import { format } from 'date-fns';
import type {
  TemplateItem,
  TemplatePaginatedResult,
  TemplateQueryError,
  TemplateQueryResponse,
} from './types/templateItem';
import { toast } from 'sonner';

const PAGE_SIZE = 20;

export const PaginatedTemplateTable = ({
  idExisting,
  setTemplateIdsToSave,
}: {
  idExisting: { resourceId: string; type: string }[];
  setTemplateIdsToSave: React.Dispatch<React.SetStateAction<{ resourceId: string; type: string }[]>>;
}) => {
  const { t } = useTranslation();
  const [pageIndex, setPageIndex] = useState(0);
  const createKey = (resourceId: string, type: string) => `${type}:${resourceId}`;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(idExisting.map(({ resourceId, type }) => createKey(resourceId, type))),
  );
  const [searchValue, setSearchValue] = useState('');
  const debounceSearch = useDebounce((s) => setSearchValue(s), 500);
  const token = getManagerAccessToken();
  const { data, loading } = useQuery<{ templatesv2: TemplateQueryResponse }>(PAGINATED_TEMPLATES, {
    variables: {
      input: {
        token: token,
        filter: {
          context: 'current',
          name: searchValue,
          visibility: ['local'],
        },
        pageIndex: pageIndex ?? 0,
        pageSize: PAGE_SIZE,
        sortingState: [{ id: 'created_at', desc: true }],
      },
    },
  });

  const item = data?.templatesv2?.item;

  const templates: TemplateItem[] =
    item?.__typename === 'TemplatePaginatedResult' ? ((item as TemplatePaginatedResult).data ?? []) : [];

  const totalPages: number =
    item?.__typename === 'TemplatePaginatedResult' ? ((item as TemplatePaginatedResult).totalPages ?? 1) : 1;

  useEffect(() => {
    if (item?.__typename === 'TemplateQueryError') {
      toast.error(`GraphQL error: ${(item as TemplateQueryError).message}`);
    }
  }, [item]);

  const isSelected = (resourceId: string, type: string) => {
    return selectedIds.has(createKey(resourceId, type));
  };

  const toggleSelect = (resourceId: string, type: string) => {
    const key = createKey(resourceId, type);
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) newSet.delete(key);
      else newSet.add(key);
      return newSet;
    });
  };

  const areAllRowsOnPageSelected =
    templates.length > 0 && templates.every((t) => selectedIds.has(createKey(t.id, 'template')));

  const toggleSelectAll = () => {
    const newSet = new Set(selectedIds);
    if (areAllRowsOnPageSelected) {
      templates.forEach((t) => {
        newSet.delete(createKey(t.id, 'template'));
      });
    } else {
      templates.forEach((t) => {
        newSet.add(createKey(t.id, 'template'));
      });
    }
    setSelectedIds(newSet);
  };

  const handlePageChange = (page: number) => {
    setPageIndex(page - 1);
  };

  useEffect(() => {
    setTemplateIdsToSave(
      Array.from(selectedIds).map((key) => {
        const [type, resourceId] = key.split(':');
        return { resourceId, type };
      }),
    );
  }, [selectedIds]);

  return (
    <>
      <Flex mb="4" align="center" gap="3">
        <TextField.Root
          placeholder={t('settings.accessGroupv2.templateTable.searchPlaceholder')}
          type="text"
          className="w-full"
          size="3"
          defaultValue={searchValue}
          onChange={(e) => {
            debounceSearch(e.target.value);
          }}
        />
      </Flex>
      <Table.Root variant="surface" size="2" layout="auto">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell width="1">
              <Checkbox checked={areAllRowsOnPageSelected} onCheckedChange={toggleSelectAll} />
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>{t('settings.accessGroupv2.templateTable.columns.name')}</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>
              {t('settings.accessGroupv2.templateTable.columns.processId')}
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>{t('settings.accessGroupv2.templateTable.columns.status')}</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>
              {t('settings.accessGroupv2.templateTable.columns.visibility')}
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>
              {t('settings.accessGroupv2.templateTable.columns.createdAt')}
            </Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {loading && (
            <Table.Row>
              <Table.Cell colSpan={6}>
                <Text size="2">{t('settings.accessGroupv2.templateTable.loading')}</Text>
              </Table.Cell>
            </Table.Row>
          )}

          {!loading && templates.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={6}>
                <Text size="2">{t('settings.accessGroupv2.templateTable.empty')}</Text>
              </Table.Cell>
            </Table.Row>
          )}

          {!loading &&
            templates.map((template) => (
              <Table.Row key={template.id}>
                <Table.Cell>
                  <Checkbox
                    checked={isSelected(template.id, 'template')}
                    onCheckedChange={() => toggleSelect(template.id, 'template')}
                  />
                </Table.Cell>
                <Table.Cell>{template.name}</Table.Cell>
                <Table.Cell>{template.processId}</Table.Cell>
                <Table.Cell>{template.templateStatus}</Table.Cell>
                <Table.Cell>{template.visibility ?? '-'}</Table.Cell>
                <Table.Cell>{format(new Date(template.created.date), 'PPP')}</Table.Cell>
              </Table.Row>
            ))}
        </Table.Body>
      </Table.Root>
      {selectedIds.size > 0 && (
        <Flex justify="between" align="center" px="4" py="3" mt="2">
          <Text size="2" weight="medium">
            {selectedIds.size === 1
              ? t('settings.accessGroupv2.templateTable.selectedCountSingular', { count: selectedIds.size })
              : t('settings.accessGroupv2.templateTable.selectedCountPlural', { count: selectedIds.size })}
          </Text>
          <Button variant="ghost" color="gray" onClick={() => setSelectedIds(new Set())}>
            {t('settings.accessGroupv2.templateTable.clearSelectionButton')}
          </Button>
        </Flex>
      )}
      <Flex justify="center" align="center" gap="2" my="4">
        <Pagination totalPages={totalPages} currentPage={pageIndex + 1} onPageChange={handlePageChange} />
      </Flex>
    </>
  );
};
